import { useEffect, useRef, useState } from 'react';
import { AlignJustify, ChevronRight, LogOut, Moon, Search, Sun } from 'lucide-react';
import type { CampaignSummary } from '../../../shared/types';
import UndoRedoControls from '../UndoRedoControls';
import Tooltip from '../ui/Tooltip';

interface AppHeaderProps {
  canUndo: boolean;
  canRedo: boolean;
  onUndo: () => void;
  onRedo: () => void;
  lastActionDescription?: string;
  nextRedoDescription?: string;
  campaigns: CampaignSummary[];
  activeCampaignId: number | null;
  campaignsLoading: boolean;
  campaignsError: string;
  selectCampaign: (campaignId: number) => void;
  onOpenSearch: () => void;
  densityMode: 'comfortable' | 'compact';
  onToggleDensity: () => void;
  theme: 'light' | 'dark';
  onToggleTheme: () => void;
  username: string;
  onLogout: () => void;
  activeTabLabel?: string;
}

export default function AppHeader({
  canUndo,
  canRedo,
  onUndo,
  onRedo,
  lastActionDescription,
  nextRedoDescription,
  campaigns,
  activeCampaignId,
  campaignsLoading,
  campaignsError,
  selectCampaign,
  onOpenSearch,
  densityMode,
  onToggleDensity,
  theme,
  onToggleTheme,
  username,
  onLogout,
  activeTabLabel,
}: AppHeaderProps) {
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isUserMenuOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setIsUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [isUserMenuOpen]);

  const initials = username ? username.charAt(0).toUpperCase() : '?';

  return (
    <div className="app-header">
      <div className="header-left">
        <select
          className="header-campaign-select"
          value={activeCampaignId ?? ''}
          onChange={(e) => selectCampaign(Number(e.target.value))}
          disabled={campaignsLoading || campaigns.length === 0}
        >
          {campaigns.length === 0 && <option value="">No campaigns</option>}
          {campaigns.map((campaign) => (
            <option key={campaign.id} value={campaign.id}>
              {campaign.name || 'Untitled Campaign'}
            </option>
          ))}
        </select>
        {campaignsError && <div className="text-[10px] text-[var(--danger)]">{campaignsError}</div>}
        {activeTabLabel && (
          <div className="header-breadcrumb">
            <ChevronRight size={12} />
            <span className="header-breadcrumb-current">{activeTabLabel}</span>
          </div>
        )}
        <div className="undo-redo-controls" style={{ opacity: 0.7 }}>
          <UndoRedoControls
            canUndo={canUndo}
            canRedo={canRedo}
            onUndo={onUndo}
            onRedo={onRedo}
            lastActionDescription={lastActionDescription}
            nextRedoDescription={nextRedoDescription}
          />
        </div>
      </div>

      <div className="header-center">
        <button
          type="button"
          className="header-search-trigger"
          onClick={onOpenSearch}
        >
          <Search size={14} />
          <span className="header-search-placeholder">Search or command...</span>
          <kbd className="header-search-shortcut">&#8984;K</kbd>
        </button>
      </div>

      <div className="header-right">
        <Tooltip content={densityMode === 'compact' ? 'Switch to comfortable layout' : 'Switch to compact layout'}>
          <button
            type="button"
            onClick={onToggleDensity}
            aria-label={densityMode === 'compact' ? 'Switch to comfortable layout' : 'Switch to compact layout'}
            className={`header-action-btn ${densityMode === 'compact' ? 'header-action-btn-active' : ''}`}
          >
            <AlignJustify size={16} />
          </button>
        </Tooltip>
        <Tooltip content={theme === 'dark' ? 'Light mode' : 'Dark mode'}>
          <button
            type="button"
            onClick={onToggleTheme}
            className={`header-action-btn ${theme === 'dark' ? 'header-action-btn-active' : ''}`}
          >
            {theme === 'dark' ? <Sun size={16} /> : <Moon size={16} />}
          </button>
        </Tooltip>
        <div className="relative" ref={userMenuRef}>
          <button
            type="button"
            onClick={() => setIsUserMenuOpen((prev) => !prev)}
            className="w-8 h-8 rounded-full bg-[var(--accent)] text-white flex items-center justify-center text-sm font-semibold cursor-pointer hover:opacity-90 transition-opacity"
            title={username}
          >
            {initials}
          </button>
          {isUserMenuOpen && (
            <div className="absolute right-0 top-full mt-1 w-48 rounded-lg border border-[var(--border-default)] bg-[var(--bg-surface)] shadow-lg z-50 py-1">
              <div className="px-3 py-2 text-xs text-[var(--text-muted)] border-b border-[var(--border-default)] truncate">
                {username}
              </div>
              <button
                onClick={() => {
                  setIsUserMenuOpen(false);
                  onLogout();
                }}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-[var(--text-secondary)] hover:bg-[var(--accent-muted)] transition-colors"
              >
                <LogOut size={14} /> Logout
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
